/**
 * Quem sumiu (plano, seção 11.2 e 17.3): clientes sem visita há mais do que o prazo
 * e sem atendimento futuro. O prazo pode ser por serviço (return_days), quando
 * o último serviço feito tiver um ciclo de retorno definido (ex.: manutenção de gel).
 */
import type { Appointment, Client, Service } from '../schemas/entities';
import { occupiesTime } from './appointments';
import { DAY, toMs } from './time';

export interface MissingClient {
  client: Client;
  lastVisitAt: string;
  lastService: Service | null;
  daysSince: number;
  weeksSince: number;
  thresholdDays: number;
}

export interface LastVisit {
  at: string;
  serviceId: string;
}

/** Última visita (atendimento concluído) por cliente. */
export function lastVisits(appointments: Appointment[]): Map<string, LastVisit> {
  const out = new Map<string, LastVisit>();
  for (const a of appointments) {
    if (a.deleted_at || a.status !== 'completed') continue;
    const prev = out.get(a.client_id);
    if (!prev || toMs(a.start_utc) > toMs(prev.at)) out.set(a.client_id, { at: a.start_utc, serviceId: a.service_id });
  }
  return out;
}

export function missingClients(params: {
  clients: Client[];
  appointments: Appointment[];
  services: Service[];
  defaultDays: number;
  now: number | string;
}): MissingClient[] {
  const now = toMs(params.now);
  const visits = lastVisits(params.appointments);
  const services = new Map(params.services.map((s) => [s.id, s]));
  const hasFuture = new Set(
    params.appointments
      .filter((a) => occupiesTime(a) && a.status !== 'completed' && toMs(a.start_utc) >= now)
      .map((a) => a.client_id),
  );

  const out: MissingClient[] = [];
  for (const client of params.clients) {
    if (client.deleted_at || hasFuture.has(client.id)) continue;
    const visit = visits.get(client.id);
    const lastAt = visit?.at ?? client.last_visit_at;
    if (!lastAt) continue;
    const lastService = visit ? (services.get(visit.serviceId) ?? null) : null;
    const thresholdDays = lastService?.return_days ?? params.defaultDays;
    const daysSince = Math.floor((now - toMs(lastAt)) / DAY);
    if (daysSince <= thresholdDays) continue;
    out.push({ client, lastVisitAt: lastAt, lastService, daysSince, weeksSince: Math.floor(daysSince / 7), thresholdDays });
  }
  // Quem sumiu há menos tempo primeiro: mais chance de voltar.
  return out.sort((a, b) => a.daysSince - b.daysSince);
}
